import React, { useEffect, useMemo, useState } from "react";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import worldAtlas from "world-atlas/countries-110m.json";
import { ISO_ALPHA2_TO_NUMERIC } from "@/lib/iso3166";
import { flagEmoji } from "@/lib/countries";

// TopoJSON country geometries carry a numeric ISO 3166-1 id like "036" for
// Australia — build the reverse lookup once, then match against our backend
// data which speaks alpha-2 ("AU").
function buildLookup(countries) {
  const byNumeric = {};
  countries.forEach((row) => {
    const numeric = ISO_ALPHA2_TO_NUMERIC[row.code?.toUpperCase()];
    if (numeric) byNumeric[numeric] = row;
    // world-atlas sometimes drops the leading zero — index both
    if (numeric && numeric.startsWith("0")) {
      byNumeric[numeric.replace(/^0+/, "")] = row;
    }
  });
  return byNumeric;
}

export default function WorldHeatmap({ countries = [], loading = false }) {
  const [tooltip, setTooltip] = useState(null);   // { x, y, code, country, ... }
  const [dims, setDims] = useState({ w: 800, h: 380 });

  useEffect(() => {
    const el = document.getElementById("world-heatmap-container");
    if (!el) return undefined;
    const ro = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setDims({ w: width, h: Math.max(280, Math.round(width * 0.48)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const lookup = useMemo(() => buildLookup(countries), [countries]);
  const max = useMemo(() =>
    Math.max(1, ...countries.map((c) => c.total || 0)), [countries]);

  const color = useMemo(() =>
    scaleLinear()
      .domain([0, 1, Math.ceil(max * 0.35), max])
      .range(["#F4F5F7", "#FEE4C2", "#F97316", "#B91C1C"])
      .clamp(true),
  [max]);

  return (
    <div className="relative w-full" id="world-heatmap-container">
      <ComposableMap
        projection="geoEqualEarth"
        projectionConfig={{ scale: dims.w / 6.1 }}
        width={dims.w} height={dims.h}
        style={{ width: "100%", height: dims.h, background: "#FAFAF9" }}
        data-testid="world-heatmap-svg">
        <Geographies geography={worldAtlas}>
          {({ geographies }) => geographies.map((geo) => {
            const numeric = String(geo.id).padStart(3, "0");
            const row = lookup[numeric] || lookup[String(geo.id)];
            const fill = row ? color(row.total) : "#F4F5F7";
            const stroke = row ? "#DC2626" : "#E4E4E7";
            return (
              <Geography key={geo.rsmKey} geography={geo}
                stroke={stroke} strokeWidth={row ? 0.7 : 0.3}
                fill={fill}
                onMouseEnter={(e) => {
                  if (!row) return;
                  setTooltip({ x: e.clientX, y: e.clientY, ...row });
                }}
                onMouseMove={(e) => setTooltip((t) => t ? { ...t, x: e.clientX, y: e.clientY } : t)}
                onMouseLeave={() => setTooltip(null)}
                style={{
                  default:  { outline: "none", cursor: row ? "pointer" : "default" },
                  hover:    { outline: "none", fill: row ? "#0F172A" : "#E4E4E7" },
                  pressed:  { outline: "none" },
                }}
                data-testid={row ? `heatmap-country-${row.code}` : undefined}
              />
            );
          })}
        </Geographies>
      </ComposableMap>

      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 text-xs text-zinc-500 font-mono uppercase tracking-[0.15em]">
          Loading world heat-map…
        </div>
      )}

      {countries.length === 0 && !loading && (
        <div className="absolute inset-0 flex items-center justify-center text-xs text-zinc-500 italic">
          No blocked or failed traffic recorded yet — the map lights up as attackers show up.
        </div>
      )}

      {tooltip && (
        <div className="pointer-events-none fixed z-50 bg-zinc-900 text-white text-xs px-3 py-2 rounded-sm shadow-xl"
             style={{ left: tooltip.x + 14, top: tooltip.y + 14, maxWidth: 260 }}
             data-testid="heatmap-tooltip">
          <div className="font-display font-bold text-sm flex items-center gap-1.5">
            {flagEmoji(tooltip.code)} {tooltip.country}
            <span className="text-zinc-400 text-[10px] font-mono">({tooltip.code})</span>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
            <span className="text-zinc-400">Total events</span>
            <span className="font-mono font-bold text-right">{tooltip.total}</span>
            <span className="text-zinc-400">Failed logins</span>
            <span className="font-mono text-right">{tooltip.attempts}</span>
            <span className="text-zinc-400">Blocked hits</span>
            <span className="font-mono text-right">{tooltip.blocked_hits}</span>
            <span className="text-zinc-400">Distinct IPs</span>
            <span className="font-mono text-right">{tooltip.distinct_ips}</span>
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="mt-2 flex items-center justify-between text-[10px] uppercase tracking-[0.15em] text-zinc-500">
        <span>Attack origins ({countries.length} countries)</span>
        <div className="flex items-center gap-1.5">
          <span>Fewer</span>
          <div className="flex h-2 w-40 rounded-sm overflow-hidden border border-zinc-200">
            {["#F4F5F7","#FEE4C2","#FDBA74","#F97316","#EA580C","#DC2626","#B91C1C"].map((c) => (
              <div key={c} style={{ background: c, width: "14.28%" }} />
            ))}
          </div>
          <span>More</span>
        </div>
      </div>
    </div>
  );
}
